import {
  UNIFORM_CIRCUIT_FAILURE_THRESHOLD,
  UNIFORM_CIRCUIT_OPEN_MS,
  UNIFORM_CIRCUIT_RETRY_AFTER_SECONDS,
  UNIFORM_CIRCUIT_WINDOW_MS,
} from '@/core/constants/fetch-resilience';
import UniformCircuitOpenError from '@/core/errors/uniform-circuit-open-error';
import isTransientFetchError, {
  isConnectionExhaustionError,
} from '@/core/utils/is-transient-fetch-error';

type CircuitBreakerState = {
  failureTimestamps: number[];
  openedAt: number | null;
};

const GLOBAL_CIRCUIT_KEY = '__mindoasisUniformCircuitBreaker';

function getState(): CircuitBreakerState {
  const globalScope = globalThis as typeof globalThis & {
    [GLOBAL_CIRCUIT_KEY]?: CircuitBreakerState;
  };

  if (!globalScope[GLOBAL_CIRCUIT_KEY]) {
    globalScope[GLOBAL_CIRCUIT_KEY] = {
      failureTimestamps: [],
      openedAt: null,
    };
  }

  return globalScope[GLOBAL_CIRCUIT_KEY];
}

function pruneFailures(state: CircuitBreakerState, now: number): void {
  state.failureTimestamps = state.failureTimestamps.filter(
    timestamp => now - timestamp <= UNIFORM_CIRCUIT_WINDOW_MS,
  );
}

function isCircuitOpen(now = Date.now()): boolean {
  const state = getState();

  if (state.openedAt === null) {
    return false;
  }

  if (now - state.openedAt >= UNIFORM_CIRCUIT_OPEN_MS) {
    state.openedAt = null;
    state.failureTimestamps = [];
    return false;
  }

  return true;
}

export function getUniformCircuitRetryAfterSeconds(): number {
  if (!isCircuitOpen()) {
    return 0;
  }

  const state = getState();
  if (state.openedAt === null) {
    return UNIFORM_CIRCUIT_RETRY_AFTER_SECONDS;
  }

  const remainingMs = UNIFORM_CIRCUIT_OPEN_MS - (Date.now() - state.openedAt);
  return Math.max(1, Math.ceil(remainingMs / 1000));
}

export function recordUniformCircuitSuccess(): void {
  const state = getState();
  state.failureTimestamps = [];
  state.openedAt = null;
}

export function recordUniformCircuitFailure(error: unknown): void {
  if (!isTransientFetchError(error)) {
    return;
  }

  const now = Date.now();
  const state = getState();

  pruneFailures(state, now);
  state.failureTimestamps.push(now);

  if (
    state.failureTimestamps.length >= UNIFORM_CIRCUIT_FAILURE_THRESHOLD &&
    state.openedAt === null
  ) {
    state.openedAt = now;
    console.error(
      JSON.stringify({
        code: 'UNIFORM_CIRCUIT_OPEN',
        failures: state.failureTimestamps.length,
        retryAfterSeconds: UNIFORM_CIRCUIT_RETRY_AFTER_SECONDS,
      }),
    );
  }
}

export function assertUniformCircuitClosed(): void {
  if (!isCircuitOpen()) {
    return;
  }

  throw new UniformCircuitOpenError(getUniformCircuitRetryAfterSeconds());
}

export function isUniformCircuitOpen(): boolean {
  return isCircuitOpen();
}

export function shouldSkipUniformOutboundRetry(error: unknown): boolean {
  return isConnectionExhaustionError(error);
}
